import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ApplicationSetting, CategoryApplicationSettingsConfigurationDto } from '@rocksoft/settings-sdk';
import * as fromCategoryResolvedConfigurations from './category-resolved-configuration.reducer';
import { CategoryResolvedConfigurationState } from './category-resolved-configuration.state';

export type ResolvedApplicationSettingItem = Omit<ApplicationSetting, 'inheritedFrom'> & {
  bundleIdentifier: string;
  configurationId: string;
  inheritedFrom?: ApplicationSetting['inheritedFrom'] | null;
};

const flattenApplicationSettingsConfiguration = (
  configuration: CategoryApplicationSettingsConfigurationDto
): ResolvedApplicationSettingItem[] =>
  configuration.applicationSettings.map((setting) => ({
    ...setting,
    bundleIdentifier: configuration.bundleIdentifier,
    configurationId: configuration.id,
  }));

const isDirectApplicationSetting = (setting: ResolvedApplicationSettingItem): boolean =>
  setting.inheritedFrom == null && !setting.isAutoGenerated;

const isInheritedApplicationSetting = (setting: ResolvedApplicationSettingItem): boolean =>
  setting.inheritedFrom != null && !setting.isAutoGenerated;

// ============================================================================
// STATE SELECTORS
// ============================================================================

export const selectCategoryResolvedConfigurationState = createFeatureSelector<CategoryResolvedConfigurationState>(
  'categoryResolvedConfiguration'
);

export const selectAllCategoryResolvedConfigurations = createSelector(
  selectCategoryResolvedConfigurationState,
  fromCategoryResolvedConfigurations.selectAllCategoryResolvedConfigurations
);

export const selectCategoryResolvedConfigurationByCategoryId = (categoryId: string) =>
  createSelector(selectAllCategoryResolvedConfigurations, (configurations) =>
    configurations.find((configuration) => configuration.categoryId === categoryId)
  );

// ============================================================================
// CONFIGURATION SELECTORS PER SETTING TYPE
// ============================================================================

export const selectResolvedApplicationConfigurationByCategoryId = (categoryId: string) =>
  createSelector(
    selectCategoryResolvedConfigurationByCategoryId(categoryId),
    (configuration) => configuration?.applications
  );

export const selectResolvedApplicationConfigurationBundleIdentifierByCategoryId = (categoryId: string) =>
  createSelector(selectCategoryResolvedConfigurationByCategoryId(categoryId), (configuration) =>
    (configuration?.applications ?? [])
      .map((app) => app.bundleIdentifier)
      .filter(
        (bundleIdentifier): bundleIdentifier is string => bundleIdentifier !== undefined && bundleIdentifier !== null
      )
  );

export const selectResolvedVppApplicationConfigurationByCategoryId = (categoryId: string) =>
  createSelector(
    selectCategoryResolvedConfigurationByCategoryId(categoryId),
    (configuration) => configuration?.vppApplications
  );

export const selectResolvedVppApplicationConfigurationBundleIdentifierByCategoryId = (categoryId: string) =>
  createSelector(selectCategoryResolvedConfigurationByCategoryId(categoryId), (configuration) =>
    (configuration?.vppApplications ?? [])
      .map((app) => app.bundleIdentifier)
      .filter(
        (bundleIdentifier): bundleIdentifier is string => bundleIdentifier !== undefined && bundleIdentifier !== null
      )
  );

export const selectResolvedApplicationSettingsConfigurationByCategoryId = (categoryId: string) =>
  createSelector(
    selectCategoryResolvedConfigurationByCategoryId(categoryId),
    (configuration) => configuration?.applicationSettingsConfigurations
  );

export const selectResolvedApplicationSettingsConfigurationByCategoryIdAndBundleIdentifier = (
  categoryId: string,
  bundleIdentifier: string
) =>
  createSelector(selectResolvedApplicationSettingsConfigurationByCategoryId(categoryId), (configurations) =>
    configurations?.find((configuration) => configuration.bundleIdentifier === bundleIdentifier)
  );

export const selectResolvedApplicationSettingsByCategoryId = (categoryId: string) =>
  createSelector(
    selectResolvedApplicationSettingsConfigurationByCategoryId(categoryId),
    (configurations) => configurations?.flatMap(flattenApplicationSettingsConfiguration) ?? []
  );

export const selectResolvedApplicationSettingsByCategoryIdAndBundleIdentifier = (
  categoryId: string,
  bundleIdentifier: string
) =>
  createSelector(
    selectResolvedApplicationSettingsConfigurationByCategoryIdAndBundleIdentifier(categoryId, bundleIdentifier),
    (configuration) => (configuration ? flattenApplicationSettingsConfiguration(configuration) : [])
  );

export const selectResolvedLinkedApplicationSettingsByCategoryIdAndBundleIdentifier = (
  categoryId: string,
  bundleIdentifier: string
) =>
  createSelector(
    selectResolvedApplicationSettingsByCategoryIdAndBundleIdentifier(categoryId, bundleIdentifier),
    (settings) => settings.filter(isDirectApplicationSetting)
  );

export const selectResolvedInheritedApplicationSettingsByCategoryIdAndBundleIdentifier = (
  categoryId: string,
  bundleIdentifier: string
) =>
  createSelector(
    selectResolvedApplicationSettingsByCategoryIdAndBundleIdentifier(categoryId, bundleIdentifier),
    (settings) => settings.filter(isInheritedApplicationSetting)
  );

export const selectResolvedLinkedApplicationSettingsConfigurationByCategoryIdAndBundleIdentifier = (
  categoryId: string,
  bundleIdentifier: string
) =>
  createSelector(
    selectResolvedApplicationSettingsConfigurationByCategoryIdAndBundleIdentifier(categoryId, bundleIdentifier),
    (configuration) =>
      configuration?.applicationSettings.some((setting) => setting.inheritedFrom == null && !setting.isAutoGenerated)
        ? configuration
        : null
  );

export const selectResolvedAppLocksByCategoryId = (categoryId: string) =>
  createSelector(
    selectCategoryResolvedConfigurationByCategoryId(categoryId),
    (configuration) => configuration?.appLocks
  );

export const selectResolvedWifiProfilesByCategoryId = (categoryId: string) =>
  createSelector(
    selectCategoryResolvedConfigurationByCategoryId(categoryId),
    (configuration) => configuration?.wifiProfiles ?? []
  );

export const selectResolvedRestrictionsProfilesByCategoryId = (categoryId: string) =>
  createSelector(
    selectCategoryResolvedConfigurationByCategoryId(categoryId),
    (configuration) => configuration?.restrictionsProfiles ?? []
  );

export const selectResolvedAirplaySecurityProfileByCategoryId = (categoryId: string) =>
  createSelector(
    selectCategoryResolvedConfigurationByCategoryId(categoryId),
    (configuration) => configuration?.airplaySecurityProfile ?? null
  );
